/* eslint-disable react/no-unescaped-entities */

import { useMemo } from 'react';
import UIButton from 'components/Others/UIButton';
import LazyHydrate from 'react-lazy-hydration';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import Breadcrumb from 'components/Breadcrumb/Breadcrumb';
import Image from "next/legacy/image"
import HeadImage from '../../public/pd/pd-header-image.png';
import useWindowSize from 'hooks/useWindowSize';
import QuizBlock from 'components/Icons/QuizBlock';
import MenuBook from 'components/Icons/MenuBook';
import SmsFailed from 'components/Icons/SmsFailed';
import Ruler from 'components/Icons/Ruler';
//import { useDispatch } from 'react-redux';
import ShopBasketIcon from 'components/Icons/ShopBasketIcon';
import QuestionIcon from 'components/Icons/QuestionIcon';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { addDataLayer } from 'lib/ga';
import PDMeterButtonNewsletterBox from 'components/PDMeterButtonNewsletterBox/PDMeterButtonNewsletterBox';
import { getSeoMetaTags } from 'utils/common.utils';
import { SeoMetaTags } from 'constants/dataTypes';
import GlobalSeoMetaTags from 'components/GlobalSeoMetaTags/GlobalSeoMetaTags';

const PDModal = dynamic(() => import('components/PD/PDModal'), { ssr: false });

export default function PdMeter( { seoMetaTags }: { seoMetaTags: SeoMetaTags }) {

  const router = useRouter();
  //const dispatch = useDispatch();
  //const [isPDModalVisible, setIsPDModalVisible] = useState<any>(false);
  //const showPDModal = useSelector((state: any) => state.pdEvent.showPdModal);
  const { width } = useWindowSize(1240);

  type Locale = "ca" | "au" | "international";
  const locale = router.locale as Locale;

  const bookLink = () => {
    addDataLayer({ event: 'online_pd_meter_booking_click' }, false);

    const bookUrl = router.locale=="ca"?"/book-an-eye-exam":"/book-an-optometrist";
    router.push(bookUrl);
  };
  const shopNowLink = () => {
    addDataLayer({ event: 'online_pd_meter_shop_now_click' }, false);
    router.push('/shop?filters=Prescription+Glasses%2CPrescription+Sunglasses')
  };


  const data = useMemo(() => (
    [
      {
        id: 1,
        href: '',
        text: 'Dresden Vision'
      },
      {
        id: 2,
        // href: `shop/${router.query.category}`,
        href: `/online-pupillary-distance`,
        text: `Online Pupillary Distance Measurement Tool`
      }
    ]
  ), []);
  return (
    <div>
      <Head>
        <title>Online Pupillary Distance Measurement Tool | Dresden Vision</title>
        <meta
          name="twitter:title"
          content={`Online Pupillary Distance Measurement Tool | Dresden Vision`}
        />
        <meta
          name="og:title"
          content={`Online Pupillary Distance Measurement Tool | Dresden Vision`}
        />
        <meta
          name="description"
          content={`Our free online pupillary distance measurement tool makes it easy to measure your PD in minutes. Learn how to measure your PD online.`}
        />
        <meta
          property="og:description"
          content={`Our free online pupillary distance measurement tool makes it easy to measure your PD in minutes. Learn how to measure your PD online.`}
        />
        <meta
          name="twitter:description"
          content={`Our free online pupillary distance measurement tool makes it easy to measure your PD in minutes. Learn how to measure your PD online.`}
        />
      </Head>
      <div className="text-center">
        <div className='h-[470px] items-center flex relative bg-[#8c9f8b]'>
          <Image alt="Measure your Pupillary Distance"
            sizes="100vw"
            layout='fill'
            priority={true}
            objectFit={width <= 1240 ? 'cover' : 'contain'}
            src={HeadImage}></Image>
          <h1 className="mx-auto text-white drop-shadow-sm text-6xl font-medium">
            Measure Your<br /> Pupillary Distance
          </h1>
        </div>
        <div className="mx-auto max-w-[1224px] pt-4 lgx:m-3 ">
          <Breadcrumb data={data} />
          <div className="mx-auto text-left">
            <h2 className=" text-4xl font-semibold mt-16">What is Pupillary Distance?</h2>
            <p className="mt-12 text-lg max-w-3xl leading-5 text-brand-grey300">
              Pupillary distance, also known as PD, is the distance between your pupils in millimetres. It is an important measurement that is used when making eyeglasses. Having the correct PD measurement ensures your lenses are positioned accurately for great vision.
            </p>
            <div className="flex justify-between mt-12 lgx:block">
              <PDMeterButtonNewsletterBox />
              <div className="lg:min-w-[391px] lgx:mb-4 border lg:mr-6 border-brand-grey500 p-7 rounded-[20px]">
                <QuizBlock />
                <h3 className="text-lg leading-5 text-brand-black100 font-semibold mt-4 mb-6">How to Measure Pupillary Distance Online with Our Free Tool</h3>
                <p className=' text-brand-grey300 text-base'>It’s easy to measure your pupillary distance online using our free tool. Our online pupillary distance measurement tool takes just a few moments, and you can measure your PD in the comfort of your own home. All you need is the camera from your computer or your smart phone. </p>
              </div>
              <div className="lg:min-w-[391px] lgx:mb-4 border border-brand-grey500 p-7 rounded-[20px]">
                <MenuBook />
                <h3 className="text-lg leading-5 text-brand-black100 font-semibold mt-4 mb-6">To use our tool, follow these simple steps</h3>
                <p className=' text-brand-grey300 text-base'>
                  {`Click on the “Measure your PD button” to the left. Place your phone or computer screen about an arms length away from your face. Face a bright light source, with no light coming from behind you. Remove your glasses before you begin. Align your face within the green guideline and hold still for 3 seconds. Receive your PD measurement and order your glasses. `}
                </p>
              </div>
            </div>




            <div className='flex  flex-wrap mt-8  lgx:block'>
              
              <div className={((router.locale=="ca"||router.locale=="au")?"lg:pr-2 lg:w-1/2":"w-full")}>
                <div className="flex flex-col justify-start border border-brand-grey500 h-full p-7 rounded-[20px] bg-[#ECEDFF]">
                  <ShopBasketIcon />
                  <h3 className="text-lg leading-5 text-brand-black100 font-semibold mt-4 mb-6">Ready to Order Your Prescription Glasses?</h3>
                  <p className=' text-brand-grey300 flex-grow text-base'>You can conveniently purchase your prescription glasses online.
Click the "<span className={'font-semibold'}>Shop Now</span>" button to explore our collection. </p>
                <UIButton
                  className="mt-10 leading-5 lg:w-72 justify-self-end h-full"
                  onClick={shopNowLink}>
                  Shop Now
                </UIButton>
                </div>
              </div>
              {(router.locale=="ca"||router.locale=="au") && (<div className="lg:pl-2 lg:w-1/2 mt-2">
              <div className="flex flex-col justify-start border border-brand-grey500 h-full p-7 rounded-[20px] bg-[#FFC6C9]">

                  <QuestionIcon />
                  <h3 className="text-lg leading-5 text-brand-black100 font-semibold mt-4 mb-6">Need a New Prescription or Help with PD Measurement?</h3>
                  <p className=' text-brand-grey300 text-base'>
                  Whether you require a new prescription or assistance with PD measurement, we're here to help.   <br />
                  <Link className='font-semibold' href={"#"} onClick={bookLink}> Book an appointment</Link> or <Link className='font-semibold' href="/stores"> visit our stores</Link> for personalized assistance. 
                  Alternatively, you can reach out to us via phone at 
                  {locale == "au" && (<><a href="tel:0253003003">{` (02) 5300 3003`}</a> or email us at <a href="mailto:hello@dresden.vision">hello@dresden.vision</a></>)}
                  {locale == "ca" && (<><a href="tel:6474244288">{` (647) 424 4288`}</a> or email us at <a href="mailto:hello@ca.dresden.vision">hello@ca.dresden.vision</a></>)} 
                  {locale == "international" && (<><a href="tel:12132236100">{` +1 (213) 223-6100`}</a> or email us at <a href="mailto:hello@int.dresden.vision">hello@int.dresden.vision</a></>)}
                  </p>
                  <UIButton
                  className="mt-10 leading-5 w-full lg:w-72 justify-self-end h-full"
                  onClick={bookLink}>
                  {router.locale=="ca"?"Book an eye exam":"Book an Optometrist"}
                </UIButton>
                </div>
              </div>)}
              <div className="lg:pr-2 lg:pt-4 lg:w-1/2 ">
                <div className="border border-brand-grey500 h-full p-7 rounded-[20px]">
                  <SmsFailed />
                  <h3 className=" text-lg leading-5 text-brand-black100 font-semibold mt-4 mb-4">The Importance of Measuring <br />Pupillary Distance</h3>

                  <p className='text-brand-grey300 text-base'>Measuring your pupillary distance is important because it affects the way you see. If your PD measurement is inaccurate, your eyeglasses will not be correctly centred on your eyes, which can cause eye strain and headaches. This is why it is crucial to measure your PD accurately.</p>
                </div>
              </div>
              <div className="lg:pl-2 lg:pt-4 lg:w-1/2">
                <div className="border border-brand-grey500 h-full p-7 rounded-[20px]">
                  <Ruler />
                  <h3 className=" text-lg leading-5 text-brand-black100 font-semibold mt-4 mb-4">The Traditional Method of <br />Measuring Pupillary Distance</h3>
                  <p className='text-brand-grey300 text-base'>Traditionally, pupillary distance is measured by an optometrist or ophthalmologist using a pupillometer. A pupillometer is a device that measures the distance between the pupils. The process of measuring pupillary distance is painless and takes only a few minutes to complete. </p>
                </div>
              </div>
            </div>



            <div className='p-10 bg-[#e7e8ffcc] rounded-3xl mt-8'>
              <h3 className=" text-2xl font-semibold mb-4 text-brand-black100">The Advantages of Using Our Online Pupillary Distance Measurement Tool</h3>

              <p className='text-brand-grey300 text-base'>There are several advantages to using our online pupillary distance measurement tool. Firstly, our tool is completely free to use. No need to visit an optometrist just to measure your PD, and no need to spend money on complicated measuring tools. All you need is your computer, and our online measurement tool does the rest. Secondly, our tool is incredibly easy to use. Even if you’re not tech-savvy, you can have your PD measured in a matter of minutes.
                <br /><br />Anyone can use our online tool – all you need is a computer or a smartphone with a camera. Thirdly, our tool is accurate. Our team of experts has spent countless hours developing and refining our online measurement tool to ensure that it is as accurate as possible. You can confidently use our online tool to measure your PD, knowing that you’re getting a precise and reliable result.
                <br /><br />Overall, our online pupillary distance measurement tool makes it convenient and effortless to measure your PD from anywhere at any time. By using our tool, you can ensure accurate and reliable results in just a few simple steps. Don't let the lack of PD measurements limit your eyewear options, try our tool today and get the perfect pair of glasses.</p>

            </div>


            {/* <UIButton
              className="mx-auto mt-10"
              onClick={() => setIsPDModalVisible(true)}>
              Measure your PD
            </UIButton> */}
            <LazyHydrate whenIdle>
              <PDModal />
            </LazyHydrate>
          </div>
        </div>
      </div>
      <GlobalSeoMetaTags  seoMetaTags={seoMetaTags} />
    </div>
  );
}




export const getStaticProps = async ({
  locale
}: {
  locale: string;
}) => {
 
    const seoMetaTags = await getSeoMetaTags( '/online-pupillary-distance', locale);

    return {
      props: {
        seoMetaTags
      }
    };
  
  
};